import React from "react";
import "../styles/About.css";

/* ─── Icons ─── */
const ConvertIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const ZapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const LockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ─── Data ─── */
const STATS = [
  { value: "50,000+", label: "Active Users" },
  { value: "10M+", label: "Files Converted" },
  { value: "120+", label: "Countries Served" },
];

const VALUES = [
  {
    icon: <ShieldIcon />,
    title: "Security First",
    desc: "256-bit AES encryption protects every file. All uploads are automatically deleted after processing — your data never lingers on our servers.",
    color: "#EFF6FF",
  },
  {
    icon: <ZapIcon />,
    title: "Lightning Performance",
    desc: "GPU-accelerated processing delivers instant results. Our distributed infrastructure ensures conversions complete in seconds, not minutes.",
    color: "#FFFBEB",
  },
  {
    icon: <LockIcon />,
    title: "User Privacy",
    desc: "We never sell your data. Fully GDPR and SOC 2 compliant. Your files are yours — we're just the engine that transforms them.",
    color: "#ECFDF5",
  },
];

const TEAM = [
  { name: "Swapnil Dixit", role: "Founder & CEO", bio: "Former Data Engineer at PayPal. Passionate about making enterprise-grade tools accessible to everyone.", initials: "SD", gradient: "linear-gradient(135deg, #2563EB, #7C3AED)" },
];

/* ─── Navbar ─── */
function Navbar() {
  return (
    <nav className="ab-navbar">
      <div className="ab-navbar-inner">
        <div className="ab-logo">
          <ConvertIcon />
          <span className="ab-logo-text">ConvertPro</span>
        </div>
        <div className="ab-nav-links">
          <a href="/" className="ab-nav-link">Home</a>
          <a href="/feedback" className="ab-nav-link">Feedback</a>
          <a href="/pricing" className="ab-nav-link">Pricing</a>
          <a href="/about" className="ab-nav-link ab-nav-link--active">About</a>
          <a href="/contact" className="ab-nav-link">Contact</a>
        </div>
        <a href="/signup" className="ab-nav-cta">Get Started Free</a>
      </div>
    </nav>
  );
}

/* ─── Main ─── */
export default function ConvertProAbout() {
  return (
    <div className="ab-page">
      <Navbar />

      <main className="ab-main">
        {/* Hero */}
        <div className="ab-hero">
          <h1 className="ab-title">About ConvertPro</h1>
          <p className="ab-subtitle">We're on a mission to make file conversion fast, secure, and accessible to everyone.</p>
        </div>

        {/* Our Story */}
        <div className="ab-card ab-story-card">
          <h2 className="ab-section-title">Our Story</h2>
          <p className="ab-story-text">
            ConvertPro was founded in 2026 by a team of engineers who were frustrated with the slow, insecure,
            and ad-riddled file conversion tools available online. We believed that converting a file should be
            as simple as dragging and dropping — without sacrificing security or quality.
          </p>
          <p className="ab-story-text">
            Built with a privacy-first approach from day one, ConvertPro now serves over 50,000 active users
            across 120 countries. We've processed more than 10 million files and counting — all while maintaining
            a zero-breach security record and enterprise-grade encryption on every single conversion.
          </p>
        </div>

        {/* Stats */}
        <div className="ab-stats-row">
          {STATS.map((s) => (
            <div key={s.label} className="ab-stat-card">
              <span className="ab-stat-value">{s.value}</span>
              <span className="ab-stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="ab-section">
          <h2 className="ab-section-title ab-section-title--center">Our Values</h2>
          <div className="ab-values-row">
            {VALUES.map((v) => (
              <div key={v.title} className="ab-card ab-value-card">
                <div className="ab-value-icon" style={{ background: v.color }}>{v.icon}</div>
                <h3 className="ab-value-title">{v.title}</h3>
                <p className="ab-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team */}
        <div className="ab-section">
          <h2 className="ab-section-title ab-section-title--center">Meet the Team</h2>
          <div className="ab-team-grid">
            {TEAM.map((m) => (
              <div key={m.name} className="ab-card ab-team-card">
                <div className="ab-team-avatar" style={{ background: m.gradient }}>
                  {m.initials}
                </div>
                <h3 className="ab-team-name">{m.name}</h3>
                <span className="ab-team-role">{m.role}</span>
                <p className="ab-team-bio">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="ab-cta-section">
          <h2 className="ab-cta-title">Want to Learn More?</h2>
          <p className="ab-cta-sub">Get in touch with our team — we'd love to hear from you.</p>
          <a href="#contact" className="ab-cta-btn">Contact Us</a>
        </div>
      </main>

      <footer className="ab-footer">
        <div className="ab-footer-inner">
          <p>© 2026 ConvertPro. All rights reserved.</p>
          <div className="ab-footer-links">
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms of Service</a>
            <a href="#status">Status</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
